import { Text } from 'components/Text'
import { useEffect, useMemo, useState } from 'react'
import { Wrapper } from 'styles/styleSystem/wrapper'
import SwiperCore, {
  Autoplay,
  Mousewheel,
  Navigation,
  Pagination,
  Scrollbar,
} from 'swiper'
import 'swiper/css'
import 'swiper/css/mousewheel'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/scrollbar'
import { Swiper, SwiperSlide } from 'swiper/react'
import {
  CarouselFooter,
  CarouselSlideContent,
  CarouselWrapper,
  SwiperSlidePreview,
} from './styles'

SwiperCore.use([Mousewheel, Navigation, Pagination, Autoplay])

interface CarouselProps {
  children?: React.ReactNode[]
  slidesPerView?: number
  pagination?: boolean
  scrollbar?: boolean
  autoplay?: boolean
  loop?: boolean
  footer?: boolean
  draggable?: boolean
  mouseWheel?: boolean
  forceToAxis?: boolean
  hideScrollbarBottomBorder?: boolean
  videoTitles?: string[]
  videoDescriptions?: string[]
  onSlideChange?: (slideIndex: number) => void
  currentIndex?: number
  centerSlides?: boolean
}

export const Carousel: React.FC<CarouselProps> = ({
  children = [],
  slidesPerView = 1,
  pagination = false,
  scrollbar = true,
  autoplay = false,
  footer = false,
  draggable = true,
  mouseWheel = true,
  forceToAxis = false,
  hideScrollbarBottomBorder = false,
  videoTitles = [],
  videoDescriptions = [],
  onSlideChange = (slideIndex) => null,
  currentIndex = 0,
  centerSlides = false,
}) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [swiperInstance, setSwiperInstance] = useState(null)
  const [slideDescription, setSlideDescription] = useState({
    title: null,
    description: null,
  })

  const modules = useMemo(() => {
    const _modules = [Navigation]
    if (pagination) _modules.push(Pagination)
    if (scrollbar) _modules.push(Scrollbar)
    if (autoplay) _modules.push(Autoplay)
    return _modules
  }, [pagination, scrollbar, autoplay])

  const slideTo = (index) => {
    swiperInstance.slideTo(index)
    setSelectedIndex(index)
  }

  swiperInstance?.on('slideChange', function () {
    setActiveIndex(swiperInstance.activeIndex)
  })

  useEffect(() => {
    setSlideDescription({
      title: videoTitles[selectedIndex] || null,
      description: videoDescriptions[selectedIndex] || null,
    })
  }, [selectedIndex])

  useEffect(() => {
    swiperInstance && slideTo(currentIndex)
  }, [currentIndex])

  return (
    <CarouselWrapper
      $hideScrollbarBottomBorder={hideScrollbarBottomBorder}
      $centerSlides={centerSlides}
    >
      <Swiper
        slidesPerView={slidesPerView}
        spaceBetween={10}
        scrollbar={{ draggable }}
        autoplay={autoplay}
        preventInteractionOnTransition={true}
        modules={modules}
        mousewheel={
          mouseWheel && { thresholdTime: 200, thresholdDelta: 50, forceToAxis }
        }
        pagination={pagination}
        onSlideChange={(slide) => {
          onSlideChange && onSlideChange(slide?.activeIndex)
          setSelectedIndex(slide?.clickedIndex || 0)
        }}
        onSwiper={setSwiperInstance}
      >
        {children?.map((slide, index) => (
          <SwiperSlide key={`swiper-slide-${index}`}>
            {centerSlides ? (
              <CarouselSlideContent $centerSlides={index === activeIndex + 1}>
                {slide}
              </CarouselSlideContent>
            ) : (
              slide
            )}
          </SwiperSlide>
        ))}
      </Swiper>
      {slideDescription.title && (
        <Wrapper py={20} spacing={10}>
          <Text size="4xl" weight="bold">
            {slideDescription.title}
          </Text>
          <Text size="xl" weight="light" color="secondary">
            {slideDescription.description}
          </Text>
        </Wrapper>
      )}
      {footer && swiperInstance && (
        <CarouselFooter>
          {children?.map((slide, index) => (
            <SwiperSlidePreview
              key={`swiper-preview-${index}`}
              selected={selectedIndex === index}
              onClick={() => slideTo(index)}
            >
              {slide}
            </SwiperSlidePreview>
          ))}
        </CarouselFooter>
      )}
    </CarouselWrapper>
  )
}
